import { normalizeText } from 'normalize-text';
import { CsrfHeaderAndCookieName } from './httpClient';

import config from '../config/config';

const randomNumberGenerator = () => {
    //define a variable consisting alphabets in small and capital letter
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXTZabcdefghiklmnopqrstuvwxyz1234567890';
    const stringLength = 36;
    let randomString = '';

    //loop to select a new character in each iteration
    for (let i = 0; i < stringLength; i++) {
        const randomNumber = Math.floor(Math.random() * characters.length);
        randomString += characters.substring(randomNumber, randomNumber + 1);
    }
    return randomString;
};

export const getCsrfTokenValue = () => {
    const token = 'CS' + randomNumberGenerator();
    document.cookie = `${CsrfHeaderAndCookieName}=` + token + '; secure; path=/';
    return token;
};

const sanitiseEncodeFileName = (category, fileName) => {
    const sanitised = sanitizeFileName(fileName);
    const categoryFileName = `${category.toUpperCase()}_${sanitised}`
    return encode(categoryFileName);
}

const sanitizeFileName = (fileName) => {
    const newFileName = removeAccents(fileName)
        .replace(/"'/g, '')
        .replace(/[$!*<>#%\\{}|^~[\]()`;,:?:@=&]/g, '_')
        .replace(/ /g, '_');

    return newFileName;
};

const removeAccents = (input) => normalizeText(input
    .replace(/[\u0300-\u036f]/g, '')
    .split('')
    .map((letter) => (letter === 'ß') ? 'ss' : letter)
    .join(''));

/** @see au.gov.nsw.service.gaas.modules.casemanagement.service.FileManagementService.encode */
const encode = (fileName) => {
    const baseFileName = removeFileExtension(fileName)
    const encodedBaseName = btoa(baseFileName)
    const substitutedBaseName = encodedBaseName
        // Copied from rebate-transaction-project.
        // Avoid using Base64 characters that S3 would not like.
        .replace('+', '_')
        .replace('/', '-')
    const fileExtension = getFileExtension(fileName)
    return [substitutedBaseName, fileExtension]
        // If fileExtension is blank, don't append `.${fileExtension}`
        .filter(val => !!(val.trim()))
        .join('.')
}

const removeFileExtension = (fileName) => {
    const split = (fileName + '').split('.')
    if (split.length > 1) {
        split.pop()
        return split.join('.')
    } else {
        return fileName
    }
}

const getFileExtension = (fileName) => {
    const split = (fileName + '').split('.')
    if (split.length > 1) {
        return split.pop()
    } else {
        return ''
    }
}

const blobToBase64 = blob => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    return new Promise(resolve => {
        reader.onloadend = () => {
            resolve(reader.result);
        };
        reader.onError = (err) => {
            console.error('FileReader error for readAsDataURL', err);
            reader.reject(err);
        };
    });
};

export const encodeFile = async (file, category, appData) => {
    if (file) {
        const documentList = []
        const sanitisedFileName = sanitiseEncodeFileName(category, file.name)
        const result = {
            documentType: category,
            documentBase64: await blobToBase64(file),
            /**
             * NOTE: This must match the document key format defined in FileManagementService.
             * @see au.gov.nsw.service.gaas.modules.casemanagement.service.FileManagementService.toInternalDocumentKey
             */
            documentKey: `${appData.transactionDetails.correlationId}_${config.productCode}_${sanitisedFileName}`
        };
        if (result.documentBase64 && result.documentBase64.indexOf(';base64,') > -1) {
            const splitBase64 = result.documentBase64.split(';base64,');
            if (splitBase64 && splitBase64.length > 1) {
                result.documentBase64 = splitBase64[1];
            }
        }
        documentList.push(result)
        return documentList;
    } else {
        return null;
    }
};

/**
 * This function removes unwanted chars from filename and limit its length
 * Throws Error when filename doesn't have VALID_EXT
 */
export const gaasFilenameFitting = (filename) => {
    const VALID_EXT = /\.(jpeg|pdf|png|jpg)$/i
    const INVALID_CHARS_FILENAME = /[^a-zA-Z0-9_]/g; // the apply API doesn't like dash & space too apparently
    const MAX_FILENAME_LENGTH = 64;
    const fileExt = filename.match(VALID_EXT);

    if (!fileExt) {
        throw Error(`Invalid file extension - ${filename}`);
    }

    return filename
        .substring(0, fileExt['index'])
        .replaceAll(INVALID_CHARS_FILENAME, '')
        .substring(0, MAX_FILENAME_LENGTH - fileExt[0].length)
        .concat(fileExt[0].toLowerCase());
}

export const uploadFile = async (fileObject, name, category, appData, config) => {
    // call direct to rebate transaction upload service and not proxying through localhost (for efficiency with large data)
    // const domainModel = appData; //transformAppDataIntoDomainModel(appData);
    const userId = appData.profileConnectInfo.email || appData.transactionDetails.correlationId;
    const url = `${config.baseUrl}/api/tollrelief/uploadFile`;

    const body = {
        correlationId: appData.transactionDetails.correlationId,
        documents: await encodeFile(fileObject,category,appData, name),
        rebateType: config.productCode,
    };
    const accessToken = sessionStorage.getItem('access_token');
    const options = {
        method: 'POST',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'RebateType': config.productCode,
            'x-snsw-userid': userId,
            'x-csrf-token': getCsrfTokenValue(),
        },
        body: JSON.stringify(body)
    };
    if (accessToken) {
        options.headers['Authorization'] = 'Bearer ' + accessToken;
    }
    const response = await fetch(url, options);
    if (response.ok) {
        if (response.ok) {
            const result = await response.json();
            if (result.documents.length > 0) {
                return Promise.resolve(result.documents[0]?.uri);
            }
        }
    } else {
        const responseText = await response.text() || response.statusText;
        console.debug('response body for upload error:', responseText );
        return Promise.reject(
            (response.status === 403) ? 'Malware scanning of uploaded file failed'
                : (response.status === 400) ? 'The file contents are not valid' : responseText
        );
    }

    return Promise.reject('Failed to upload document');
};

export default uploadFile;
